import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// B-1: token is captured by the inline sanitizer in index.html before any
// analytics run, held in memory only (never localStorage/sessionStorage).
function readSetupToken() {
  let t = window.__RM_SETUP_TOKEN || "";
  try { delete window.__RM_SETUP_TOKEN; } catch (e) {}
  if (!t) {
    const l = window.location;
    const m = (l.hash && l.hash.match(/[#&]token=([^&]+)/)) ||
              (l.search && l.search.match(/[?&]token=([^&]+)/));
    if (m) {
      t = decodeURIComponent(m[1]);
      window.history.replaceState(null, "", l.pathname);
    }
  }
  return t;
}

export default function AdminSetupPage() {
  const navigate = useNavigate();
  const [token] = useState(readSetupToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/auth/setup`, {
        token, password, confirm_password: confirm,
      });
      toast.success("Password set! You can now log in.");
      navigate("/admin/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-setup-form"
            className="bg-white rounded-xl shadow-md p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#FAB710] rounded-lg flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-[#1C3A7A]" />
          </div>
          <h1 className="text-xl font-bold text-[#1C3A7A]">Set up your admin account</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Choose a password (min 12 characters with upper, lower, digit and symbol).
          This link is single-use and expires 72 hours after it was issued.
        </p>
        <input data-testid="setup-password-input" type="password" value={password}
               onChange={(e) => setPassword(e.target.value)} placeholder="New password"
               className="w-full border rounded-lg px-3 py-2 mb-3" required minLength={12} />
        <input data-testid="setup-confirm-input" type="password" value={confirm}
               onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password"
               className="w-full border rounded-lg px-3 py-2 mb-5" required minLength={12} />
        <button data-testid="setup-submit-btn" disabled={busy || !token}
                className="w-full py-2 rounded-lg bg-[#1C3A7A] text-white font-medium disabled:opacity-50">
          {busy ? "Setting password..." : "Set password"}
        </button>
        {!token && <p className="text-red-500 text-sm mt-3">Missing setup token — use the link you were given.</p>}
      </form>
    </div>
  );
}
